import { Injectable, Injector, signal } from '@angular/core';
import { ISidenavLayout, SidenavLayout } from '../tokens/layout.token';
import { SidenavRightPortalService } from './sidenav-right-portal.service';

/**
 * Root-level handle to the sidenav layout currently on screen. `SidenavRightPortalService` is scoped to
 * the layout component, so code living outside of it (e.g. the AI assistant drawer rendered at the app
 * root) reaches the right panel through this registry instead.
 */
@Injectable({ providedIn: 'root' })
export class SidenavLayoutRegistry {
  private readonly _layout = signal<ISidenavLayout | null>(null);

  /** The active layout, or `null` outside of a sidenav layout page. */
  readonly layout = this._layout.asReadonly();

  register(layout: ISidenavLayout): void {
    this._layout.set(layout);
  }

  unregister(layout: ISidenavLayout): void {
    if (this._layout() === layout) this._layout.set(null);
  }

  /** A right-portal service bound to the active layout, or `null` when there is none. */
  createRightPortal(parent: Injector): SidenavRightPortalService | null {
    const layout = this._layout();
    if (!layout) return null;
    return Injector.create({
      parent,
      providers: [{ provide: SidenavLayout, useValue: layout }, SidenavRightPortalService]
    }).get(SidenavRightPortalService);
  }
}
